/* eslint-disable @typescript-eslint/no-explicit-any */

import getConfig from "next/config";
import { isEmail } from "validator";

const { publicRuntimeConfig } = getConfig();

export const getLabel = (
  options: { label: string; value: string }[],
  value: string
): string => {
  const option = options.find((option) => option.value === value);
  if (!option) return null;
  return option.label;
};

export const transformInToFormObject = (
  data: Record<string, any>
): FormData => {
  const formData = new FormData();
  for (const key in data) {
    if (Array.isArray(data[key])) {
      data[key].forEach((obj, index) => {
        const keyList = Object.keys(obj);
        keyList.forEach((keyItem) => {
          const keyName = [key, "[", index, "]", ".", keyItem].join("");
          formData.append(keyName, obj[keyItem]);
        });
      });
    } else if (typeof data[key] === "object") {
      for (const innerKey in data[key]) {
        formData.append(`${key}.${innerKey}`, data[key][innerKey]);
      }
    } else {
      formData.append(key, data[key]);
    }
  }

  return formData;
};

export const dataURLtoBlob = (dataurl: string): Blob => {
  const arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]);

  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

export const dataURLtoBlobWithMime = (
  dataurl: string
): { file: Blob; mime: string } => {
  const arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1];

  return {
    file: dataURLtoBlob(dataurl),
    mime,
  };
};

export const dataURLtoFileList = (
  dataurl: string,
  fileName: string
): FileList => {
  const blobObject = dataURLtoBlobWithMime(dataurl);
  const fileObject = new File([blobObject.file], fileName, {
    type: blobObject.mime,
  });
  const dt = new DataTransfer();

  dt.items.add(fileObject);

  return dt.files;
};

export const prependURL = (url: string): string => {
  url = url.trim();

  if (/^\.*\/|^(?!localhost)\w+?:/.test(url)) {
    return url;
  }

  if (isEmail(url)) {
    return url.replace(/^(?!(?:\w+?:)?\/\/)/, "mailto://");
  }

  return url.replace(/^(?!(?:\w+?:)?\/\/)/, "http://");
};

export type ImageSize = { width: number; height: number };

export const imageSize = (url: string): Promise<ImageSize> => {
  if (!url) return null;

  const img = document.createElement("img");

  const promise = new Promise((resolve: (value: ImageSize) => void, reject) => {
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      resolve({ width, height });
    };

    img.onerror = reject;
  });

  img.src = url;

  return promise;
};

export const groupArrayBy = (
  array: any[],
  by: (v: any) => string
): Record<string, any[]> => {
  return array?.reduce(
    (r, v, i, a, k = by(v)) => ((r[k] || (r[k] = [])).push(v), r),
    {}
  );
};

export const getIntOrFloat = (number: number): number | string =>
  number % 1 === 0 ? number : number?.toFixed(2);

export const S3_BASE_URL =
  "https://accred-media.fra1.cdn.digitaloceanspaces.com";

export const getS3Url = (s3Key: string): string | null => {
  if (!s3Key) return null;
  return `${S3_BASE_URL}/${s3Key}`;
};

export const getFileUri = (filePath: string): string => {
  if (!filePath) return "";

  if (
    filePath.startsWith("picture-") ||
    filePath.startsWith("file-") ||
    /^\d+-/.test(filePath) //Regex that checks if the filePath is a number-prefixed string - this is used for the accreditation photo
  ) {
    return `${publicRuntimeConfig.API_URL}/uploads/${filePath}`;
  }

  // If URI starts with /uploads/http - remove /uploads/ prefix (incorrectly saved URLs from Google, Facebook, etc.)
  if (filePath.startsWith("/uploads/http"))
    return filePath.replace("/uploads/", "");

  // If URI starts with http - return it
  if (filePath.startsWith("http")) return filePath;

  // If already contains /api/s3/ - return as is to prevent doubling
  if (filePath.startsWith(`${publicRuntimeConfig.basePath}/api/s3/`)) {
    return filePath;
  }

  // Check if it's an S3 key (contains one of the main folders prefix)
  if (
    filePath.includes("dev/") ||
    filePath.includes("staging/") ||
    filePath.includes("test/")
  ) {
    const proxyUrl = `${publicRuntimeConfig.basePath}/api/s3/${filePath}`;

    return proxyUrl;
  }

  // Old URLs
  const oldUrl = `${publicRuntimeConfig.API_URL}${filePath.replace(
    "public",
    ""
  )}`;

  return oldUrl;
};

export const getCloudPreviewUriToCopy = (filename: string): string => {
  if (!filename) return "";

  return `${publicRuntimeConfig.APP_URL}/cloud/preview/${filename}`;
};

export const downloadFile = async (
  urlOrBlob: string | Blob,
  fileName: string
): Promise<void> => {
  const link = document.createElement("a");
  let blobUrl: string;

  if (typeof urlOrBlob === "string") {
    try {
      const response = await fetch(urlOrBlob);
      const blob = await response.blob();
      blobUrl = URL.createObjectURL(blob);
    } catch (error) {
      throw error;
    }
  } else {
    blobUrl = URL.createObjectURL(urlOrBlob);
  }

  link.href = blobUrl;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(blobUrl);
};
